import React from "react";
import { canonical } from "../data/business";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import { services } from "../data/services";
import { locations } from "../data/locations";
import { projects } from "../data/projects";
import { blogs, findBlog } from "../data/blogs";
import { useParams, Navigate } from "react-router-dom";
import { ArrowRight, Clock, Calendar } from "lucide-react";

export function ServicesIndex() {
  return (
    <div className="page-enter">
      <Seo title="Landscaping Services Scotland — Last Cut Landscaping" description="Full range of landscaping services across Scotland — artificial grass, patios, driveways, decking, fencing, garden design and more." canonical={canonical("/services")} />
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-10 lg:pt-14 pb-16">
        <Breadcrumbs items={[{ label: "Services" }]} />
        <h1 className="text-display font-display text-5xl lg:text-7xl text-dark mt-6">Our Services</h1>
        <p className="text-dark/70 text-lg max-w-2xl mt-4">From a single artificial lawn to a complete garden makeover — twelve specialised services, one trusted team.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {services.map((s) => (
            <Link key={s.slug} to={`/services/${s.slug}`} className="lc-card group">
              <div className="aspect-[4/3] overflow-hidden"><img src={s.image} alt={s.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/></div>
              <div className="p-5"><h3 className="font-display text-xl text-dark">{s.name}</h3><p className="text-sm text-dark/65 mt-2 line-clamp-2">{s.short}</p></div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

export function LocationsIndex() {
  return (
    <div className="page-enter">
      <Seo title="Areas Covered — Landscaping Across Scotland | Last Cut" description="Landscapers covering Edinburgh, Glasgow, Falkirk, Livingston, Stirling and the entire Central Belt of Scotland." canonical={canonical("/locations")} />
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-10 lg:pt-14 pb-16">
        <Breadcrumbs items={[{ label: "Areas" }]} />
        <h1 className="text-display font-display text-5xl lg:text-7xl text-dark mt-6">Areas We Cover</h1>
        <p className="text-dark/70 text-lg max-w-2xl mt-4">From Edinburgh to Glasgow and everywhere between — landscaping across the entire Central Belt of Scotland.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {locations.map((l) => (
            <Link key={l.slug} to={`/locations/${l.slug}`} className="lc-card group">
              <div className="aspect-[4/3] overflow-hidden"><img src={l.image} alt={l.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/></div>
              <div className="p-5"><h3 className="font-display text-xl text-dark">Landscapers {l.name}</h3><p className="text-sm text-dark/65 mt-1">{l.region}</p></div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

export function ProjectsPage() {
  const [filter, setFilter] = React.useState("all");
  const list = filter === "all" ? projects : projects.filter((p) => p.service === filter || p.location === filter);
  return (
    <div className="page-enter">
      <Seo title="Landscaping Projects Scotland — Portfolio | Last Cut" description="Recent landscaping projects across Edinburgh, Glasgow, Falkirk and the Central Belt of Scotland." canonical={canonical("/projects")} />
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-10 lg:pt-14 pb-20">
        <Breadcrumbs items={[{ label: "Projects" }]} />
        <h1 className="text-display font-display text-5xl lg:text-7xl text-dark mt-6">Recent Projects</h1>
        <p className="text-dark/70 text-lg max-w-2xl mt-4">Real Scottish gardens — built to last and photographed proudly.</p>
        <div className="flex flex-wrap gap-2 mt-8">
          <FilterBtn active={filter==="all"} onClick={()=>setFilter("all")}>All</FilterBtn>
          {services.slice(0,6).map((s)=><FilterBtn key={s.slug} active={filter===s.slug} onClick={()=>setFilter(s.slug)}>{s.name}</FilterBtn>)}
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
          {list.map((p) => (
            <Link key={p.slug} to={`/projects/${p.slug}`} className="lc-card group" data-testid={`project-card-${p.slug}`}>
              <div className="aspect-[4/3] overflow-hidden"><img src={p.after} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/></div>
              <div className="p-5">
                <div className="pill mb-2">{p.location}</div>
                <h3 className="font-display text-lg text-dark">{p.title}</h3>
                <p className="text-sm text-dark/65 mt-1.5">{p.summary}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

const FilterBtn = ({ active, ...p }) => <button {...p} className={`px-4 py-2 rounded-full text-sm font-semibold transition ${active ? "bg-dark text-white" : "bg-paper text-dark hover:bg-dark/10"}`} />;

export function BlogIndex() {
  return (
    <div className="page-enter">
      <Seo title="Landscaping Blog Scotland — Tips, Guides & Costs | Last Cut" description="Expert landscaping guides for Scottish gardens — costs, materials, drainage, design and seasonal advice." canonical={canonical("/blog")} />
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-10 lg:pt-14 pb-20">
        <Breadcrumbs items={[{ label: "Blog" }]} />
        <h1 className="text-display font-display text-5xl lg:text-7xl text-dark mt-6">Insights & Guides</h1>
        <p className="text-dark/70 text-lg max-w-2xl mt-4">Practical landscaping knowledge for Scottish homeowners — costs, materials, design and what actually works in our climate.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {blogs.map((b) => (
            <Link key={b.slug} to={`/blog/${b.slug}`} className="lc-card group">
              <div className="aspect-[16/10] overflow-hidden"><img src={b.cover} alt={b.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/></div>
              <div className="p-5">
                <div className="flex items-center gap-3 text-xs text-dark/55 mb-2"><div className="pill">{b.category}</div><span className="flex items-center gap-1"><Clock size={12}/>{b.readTime} min</span></div>
                <h3 className="font-display text-lg text-dark">{b.title}</h3>
                <p className="text-sm text-dark/65 mt-2 line-clamp-2">{b.excerpt}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const b = findBlog(slug);
  if (!b) return <Navigate to="/blog" replace />;
  const articleJsonLd = { "@context": "https://schema.org", "@type": "BlogPosting", "headline": b.title, "image": b.cover, "datePublished": b.date, "author": { "@type": "Organization", "name": "Last Cut Landscaping" } };
  return (
    <div className="page-enter">
      <Seo title={`${b.title} | Last Cut Landscaping`} description={b.excerpt} canonical={canonical(`/blog/${b.slug}`)} jsonLd={articleJsonLd} />
      <article className="max-w-3xl mx-auto px-5 lg:px-8 pt-10 lg:pt-14 pb-20">
        <Breadcrumbs items={[{ label: "Blog", to: "/blog" }, { label: b.title }]} />
        <div className="mt-6 flex items-center gap-3 text-xs text-dark/55"><div className="pill">{b.category}</div><span className="flex items-center gap-1"><Calendar size={12}/>{b.date}</span><span className="flex items-center gap-1"><Clock size={12}/>{b.readTime} min read</span></div>
        <h1 className="text-display font-display text-4xl lg:text-6xl text-dark mt-4">{b.title}</h1>
        <p className="text-lg text-dark/70 mt-4">{b.excerpt}</p>
        <div className="rounded-2xl overflow-hidden aspect-[16/9] mt-8"><img src={b.cover} alt={b.title} className="w-full h-full object-cover"/></div>
        <div className="prose-blog mt-10" dangerouslySetInnerHTML={{ __html: b.body }} />
        <div className="mt-12 p-6 bg-dark text-white rounded-2xl">
          <h3 className="font-display text-2xl">Thinking about a project?</h3>
          <p className="text-white/70 text-sm mt-2">Free quotes across the Central Belt. We'll be in touch within 24 hours.</p>
          <Link to="/quote" className="btn-accent mt-4">Get my free quote <ArrowRight size={16}/></Link>
        </div>
      </article>
    </div>
  );
}
